import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { admin } from './admin-api';

// Superadmin Data Feeds: enter a provider's API key to turn its commodity feeds live (stored
// encrypted, never shown again), and toggle individual feeds on/off — no server access needed.
const SOURCE_TAG: Record<string, string> = { admin: 'olive', env: 'blue', none: 'muted' };
const SOURCE_LABEL: Record<string, string> = {
  admin: 'key set (in app)',
  env: 'using server env key',
  none: 'no key',
};

export default function DataFeeds() {
  const qc = useQueryClient();
  const data = useQuery({ queryKey: ['admin', 'connectors'], queryFn: admin.connectors });
  const [keyInput, setKeyInput] = useState<Record<string, string>>({});
  const [err, setErr] = useState<string | null>(null);

  const invalidate = () => qc.invalidateQueries({ queryKey: ['admin', 'connectors'] });
  const fail = (e: unknown, what: string) => setErr(e instanceof Error ? e.message : `Failed to ${what}`);

  const saveKey = useMutation({
    mutationFn: ({ provider, key }: { provider: string; key: string }) => admin.setConnectorKey(provider, key),
    onSuccess: (p) => { setErr(null); setKeyInput((s) => ({ ...s, [p.provider]: '' })); invalidate(); },
    onError: (e) => fail(e, 'save key'),
  });
  const clearKey = useMutation({
    mutationFn: (provider: string) => admin.clearConnectorKey(provider),
    onSuccess: () => { setErr(null); invalidate(); },
    onError: (e) => fail(e, 'clear key'),
  });
  const toggleFeed = useMutation({
    mutationFn: ({ commodity_id, enabled }: { commodity_id: string; enabled: boolean }) =>
      admin.setConnectorFeed(commodity_id, enabled),
    onSuccess: () => { setErr(null); invalidate(); },
    onError: (e) => fail(e, 'toggle feed'),
  });

  if (data.isLoading) return <div className="small faint">Loading data feeds…</div>;
  if (data.isError) return <div className="small faint">Data feeds unavailable.</div>;
  const { providers, feeds } = data.data!;

  return (
    <div>
      <div className="ph">Admin · Data Feeds</div>
      <div className="h2">Connect live data feeds</div>
      <p className="lead">
        Enter a provider's API key to turn its commodity feeds live, and toggle individual feeds on or
        off — no server access needed. Keys are stored encrypted and never shown again.
      </p>
      {err && <div className="login-err">{err}</div>}

      <section className="panel" style={{ marginBottom: 14 }}>
        <div className="qhead"><span className="qdot" style={{ background: 'var(--amber)' }} />Provider API keys</div>
        <table>
          <thead><tr><th>Provider</th><th>Status</th><th>Set / replace key</th></tr></thead>
          <tbody>
            {providers.map((p) => (
              <tr key={p.provider}>
                <td><b>{p.provider.toUpperCase()}</b></td>
                <td>
                  <span className={`tag ${SOURCE_TAG[p.key_source] ?? 'muted'}`}>{SOURCE_LABEL[p.key_source] ?? p.key_source}</span>
                  {p.updated_at && <div className="tiny faint">updated {new Date(p.updated_at).toLocaleDateString()}</div>}
                </td>
                <td>
                  <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                    <input
                      type="password"
                      autoComplete="off"
                      placeholder={`${p.provider.toUpperCase()}_API_KEY`}
                      value={keyInput[p.provider] ?? ''}
                      onChange={(e) => setKeyInput((s) => ({ ...s, [p.provider]: e.target.value }))}
                      style={{ flex: 1, minWidth: 0 }}
                    />
                    <button
                      type="button"
                      className="btn primary"
                      disabled={!(keyInput[p.provider] ?? '').trim() || saveKey.isPending}
                      onClick={() => saveKey.mutate({ provider: p.provider, key: (keyInput[p.provider] ?? '').trim() })}
                    >
                      Save
                    </button>
                    {p.key_source === 'admin' && (
                      <button type="button" className="btn ghost" onClick={() => clearKey.mutate(p.provider)}>Clear</button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      <section className="panel">
        <div className="qhead"><span className="qdot" style={{ background: 'var(--blue)' }} />Feeds</div>
        <p className="small faint" style={{ marginTop: -4, marginBottom: 10 }}>
          A disabled feed is not scanned for breaches, even when its provider key is set.
        </p>
        <table>
          <thead><tr><th>Feed</th><th>Provider</th><th className="center">Status</th></tr></thead>
          <tbody>
            {feeds.map((f) => (
              <tr key={f.commodity_id}>
                <td>{f.label}<div className="tiny faint mono">{f.commodity_id}</div></td>
                <td className="mono small">{f.provider.toUpperCase()}</td>
                <td className="center">
                  <button
                    type="button"
                    className={`tag ${f.enabled ? 'olive' : 'muted'}`}
                    style={{ cursor: 'pointer', border: 'none' }}
                    aria-pressed={f.enabled}
                    onClick={() => toggleFeed.mutate({ commodity_id: f.commodity_id, enabled: !f.enabled })}
                  >
                    {f.enabled ? 'on' : 'off'}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>
    </div>
  );
}
